import { Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { default as React, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import useTaskRouterMenuHandlers from '../../hooks/taskRouterHandlers/useTaskRouterMenuHandlers.hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import {
  resetSelectedRestaurantDetails,
  selectRestaurant,
} from '../../reducers/restaurantSlice';
import { startLoading } from '../../redux/features/loading/loading.slice';
import {
  selectCurrentTask,
  selectCurrentTaskRestaurantCode,
  selectCurrentTaskRestaurantName,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { selectRestaurantsById } from '../../selectors/restaurant';
import RestaurantPicker from '../RestaurantPicker';
import { LANGUAGES } from '../../utils/constants';
import useLanguages from '../../hooks/useLanguages.hooks';
import { readEnvVariable } from '../../utils';
import { getFeatureFlagValue } from '../../utils/network';
import { setMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { isDQRestaurantCode } from '../../utils/restaurants';
import { updateMenuLoadingTimings } from '../../reducers/menuSlice';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  name: {
    fontWeight: '500 !important',
    fontSize: '0.875rem !important',
    lineHeight: '1.75 !important',
    textTransform: 'uppercase',
    minWidth: spacing(8),
    padding: `${spacing(0.75)} ${spacing(2)}`,
  },
}));

const RestaurantsContainer = ({
  logout,
  sendHealthScore,
}: {
  logout: Function;
  sendHealthScore?: Function;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const updateContext = useUnleashContext();
  const isPlaying = useAppSelector((state) => state.messages.isPlaying);
  const currentTaskId = useAppSelector(selectCurrentTask);
  const {
    taskRouterMenuLoadFailureCallback,
    taskRouterMenuLoadSuccessCallback,
  } = useTaskRouterMenuHandlers();
  const currentTaskRestaurantCode = useAppSelector(
    selectCurrentTaskRestaurantCode
  );
  const currentTaskRestaurantName = useAppSelector(
    selectCurrentTaskRestaurantName
  );
  const restaurantsById = useAppSelector(selectRestaurantsById);
  const currentLanguage = useAppSelector(
    (state) => state.language.currentLanguage
  );
  const { sendLanguageSwitch } = useLanguages();

  const targetRestaurant = useMemo(() => {
    if (currentTaskRestaurantCode) {
      if (Object.keys(restaurantsById).length) {
        return Object.values(restaurantsById)?.filter(
          (restaurant) =>
            restaurant.restaurantCode === currentTaskRestaurantCode
        )?.[0];
      }
      return {
        restaurantCode: currentTaskRestaurantCode,
        restaurantName: currentTaskRestaurantName,
      };
    }
  }, [restaurantsById, currentTaskRestaurantCode, currentTaskRestaurantName]);

  const selectUpdatedRestaurant = async ({
    restaurantCode,
    startLoadingIndicator,
  }: {
    restaurantCode: string;
    startLoadingIndicator: boolean;
  }) => {
    let unleashFlagDurationMs: number;

    if (isDQRestaurantCode(restaurantCode)) {
      // DQ restaurants are always on unified menu — skip Unleash entirely
      setMenuUnificationFeatureFlag('true');
      unleashFlagDurationMs = 0;
    } else {
      const unleashStartTime = performance.now();
      await updateContext({ userId: restaurantCode });
      const flag = await getFeatureFlagValue({
        url: `${readEnvVariable('UNLEASH_API_URL')}?userId=${restaurantCode}`,
        name: 'unified_menu',
      });
      setMenuUnificationFeatureFlag(flag.toString());
      unleashFlagDurationMs = performance.now() - unleashStartTime;
    }

    if (startLoadingIndicator) {
      dispatch(startLoading({}));
    }

    // Dispatch selectRestaurant first — its fetchMenu thunk synchronously
    // resets menuLoadingTimings, so we set unleashFlagDurationMs after.
    dispatch(
      selectRestaurant({
        restaurantCode: restaurantCode,
        logout,
        taskRouterMenuLoadFailureCallback,
        taskRouterMenuLoadSuccessCallback,
      })
    );
    dispatch(updateMenuLoadingTimings({ unleashFlagDurationMs }));

    if (!isPlaying) {
      dispatch(messagingActions.setIsPlaying(true));
    }
  };

  const handleRestaurantSelect = (restaurantCode: string) => {
    sendHealthScore?.();
    if (currentTaskId) {
      selectUpdatedRestaurant({
        restaurantCode,
        startLoadingIndicator: false,
      });
      return;
    }
    dispatch(resetSelectedRestaurantDetails());
    selectUpdatedRestaurant({
      restaurantCode,
      startLoadingIndicator: true,
    });
    if (currentLanguage !== LANGUAGES.ENGLISH) {
      sendLanguageSwitch(LANGUAGES.ENGLISH);
    }
  };

  useEffect(() => {
    if (targetRestaurant) {
      handleRestaurantSelect(targetRestaurant.restaurantCode);
    }
  }, [targetRestaurant]);
  return (
    <div id="restaurants-container">
      {targetRestaurant ? (
        <Typography className={classes.name}>
          {targetRestaurant?.restaurantName}
        </Typography>
      ) : (
        <RestaurantPicker handleRestaurantSelect={handleRestaurantSelect} />
      )}
    </div>
  );
};

export default RestaurantsContainer;
